import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import UserAuthModal from './UserAuthModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const { isAuthenticated } = useUserAuth();
  const [authModalOpen, setAuthModalOpen] = useState(location.state?.openUserAuth || false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/#services' },
    { name: 'About', path: '/#about' },
    { name: 'Contact', path: '/#contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src={settings.company_logo_url}
              alt={settings.company_name}
              className="h-12 w-auto"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                className={`text-sm font-medium transition-colors duration-200 hover:text-orange-500 ${
                  isScrolled ? 'text-gray-700' : 'text-gray-800'
                }`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="/#contact"
              className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-200 hover:shadow-lg"
            >
              Get Started
            </a>
            <button
              type="button"
              onClick={() => isAuthenticated ? window.location.assign('/dashboard') : setAuthModalOpen(true)}
              className="rounded-lg border-2 border-orange-500 px-6 py-2 font-medium text-orange-600 transition-all duration-200 hover:bg-orange-500 hover:text-white hover:shadow-lg"
            >
              {isAuthenticated ? 'Dashboard' : 'Login'}
            </button>
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-800" />
            ) : (
              <Menu className="h-6 w-6 text-gray-800" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="/#contact"
              className="block px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium text-center hover:bg-orange-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </a>
            <button
              type="button"
              onClick={() => { setIsMobileMenuOpen(false); if (isAuthenticated) window.location.assign('/dashboard'); else setAuthModalOpen(true); }}
              className="block w-full rounded-lg border-2 border-orange-500 px-4 py-2 text-center font-medium text-orange-600 transition-colors hover:bg-orange-500 hover:text-white"
            >
              {isAuthenticated ? 'Dashboard' : 'Login'}
            </button>
          </div>
        </div>
      )}
      <UserAuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </header>
  );
};

export default Header;
